import React, { useMemo } from 'react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { COLORS, COLORS_DARK, SETTING_COLORS, SETTING_COLORS_DARK } from '../../constants';
import useResponsiveChart from '../../hooks/useResponsiveChart';
import CustomTooltip from '../CustomTooltip';
import ExplanationCard from '../ExplanationCard';

const SettingComparisonView = ({
  selectedMethodsForComparison,
  comparisonModel,
  methodData,
  selectedLanguages,
  experimentsMetadata,
  docCounts: propDocCounts
}) => {
  const { isDarkMode } = useTheme();
  const colors = isDarkMode ? COLORS_DARK : COLORS;
  const { chartHeight, chartMargin, axisLabelFontSize, tickFontSize, isMobile } = useResponsiveChart();
  const settingColors = isDarkMode ? SETTING_COLORS_DARK : SETTING_COLORS;

  // Group methods by setting
  const methodsBySetting = useMemo(() => {
    const groups = {
      'monolingual': [],
      'multilingual': [],
      'cross-lingual': []
    };

    selectedMethodsForComparison.forEach(method => {
      const metadata = experimentsMetadata?.[method];
      if (metadata && groups[metadata.setting]) {
        groups[metadata.setting].push(method);
      }
    });

    return groups;
  }, [selectedMethodsForComparison, experimentsMetadata]);

  // For setting comparison (comparing cross-, mono-, multi-lingual), always use [0, 10]
  // This represents baseline (0 documents) and 10 documents
  const docCounts = useMemo(() => {
    // Setting comparison always uses 0 and 10 documents
    return [0, 10];
  }, []);

  // Calculate average performance per setting per language
  const settingComparisonData = useMemo(() => {
    if (!comparisonModel || selectedMethodsForComparison.length === 0) return [];

    const dataByDocCount = docCounts.map((docCount, docIndex) => {
      const dataPoint = { docCount };

      Object.entries(methodsBySetting).forEach(([setting, methods]) => {
        if (methods.length === 0) return;

        selectedLanguages.forEach(lang => {
          const values = methods.map(method => {
            const modelData = methodData[method]?.[comparisonModel]?.[lang];
            return modelData?.[docIndex];
          }).filter(v => v != null);

          if (values.length > 0) {
            const avg = values.reduce((a, b) => a + b, 0) / values.length;
            dataPoint[`${setting}_${lang}`] = Math.round(avg * 100) / 100;
          }
        });
      });

      return dataPoint;
    });

    return dataByDocCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methodsBySetting, comparisonModel, methodData, selectedLanguages, selectedMethodsForComparison.length, docCounts]);

  // Summary statistics per setting
  const settingSummary = useMemo(() => {
    const summary = {};

    Object.entries(methodsBySetting).forEach(([setting, methods]) => {
      if (methods.length === 0) return;

      summary[setting] = {
        methodCount: methods.length,
        languages: {}
      };

      selectedLanguages.forEach(lang => {
        const allValues = [];
        methods.forEach(method => {
          const modelData = methodData[method]?.[comparisonModel]?.[lang];
          if (modelData) {
            allValues.push(...modelData.filter(v => v != null));
          }
        });

        if (allValues.length > 0) {
          const mean = allValues.reduce((a, b) => a + b, 0) / allValues.length;
          const max = Math.max(...allValues);
          const min = Math.min(...allValues);
          summary[setting].languages[lang] = { mean, max, min };
        }
      });
    });

    return summary;
  }, [methodsBySetting, methodData, comparisonModel, selectedLanguages]);

  if (selectedMethodsForComparison.length < 2 || !comparisonModel) {
    return (
      <div className="mb-8">
        <h3 className="text-3xl font-bold mb-3 text-center" style={{ color: 'var(--text-primary)' }}>
          Setting Comparison Analysis
        </h3>
        <div className="text-center py-8">
          <p style={{ color: 'var(--text-secondary)' }}>
            Select at least 2 experiments and a model to compare performance across settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <ExplanationCard title="Setting Comparison Explanation">
        <p><strong>This view:</strong> Compares average performance across monolingual, multilingual, and cross-lingual experimental settings.</p>
        <p><strong>Line patterns:</strong> Each setting (monolingual, multilingual, cross-lingual) is shown with its distinct color.</p>
        {/* <p><strong>Languages:</strong> Compare how different settings perform for each language.</p> */}
      </ExplanationCard>

      <h3 className="text-3xl font-bold mb-3 text-center" style={{ color: 'var(--text-primary)' }}>
        Setting Comparison Analysis
      </h3>
      <p className="text-lg text-center mb-8" style={{ color: 'var(--text-secondary)' }}>
        Comparing {selectedMethodsForComparison.length} experiments for {comparisonModel} model
      </p>

      {/* Performance Trends by Setting */}
      <div className="chart-wrapper mb-8">
        <h4 className="text-xl font-semibold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>
          Performance Trends by Setting
        </h4>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart data={settingComparisonData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="4 4" stroke={isDarkMode ? '#333333' : '#e0e7ff'} />
            <XAxis
              dataKey="docCount"
              label={{ value: 'Number of Documents', position: 'bottom', offset: 40, fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: axisLabelFontSize, fontWeight: 'bold' }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <YAxis
              label={{ value: 'Performance Score', angle: -90, position: 'insideLeft', offset: 10, fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: axisLabelFontSize, fontWeight: 'bold' }}
              tickLine={false}
              axisLine={{ stroke: isDarkMode ? '#404040' : '#cbd5e0' }}
              domain={['dataMin - 5', 'dataMax + 5']}
              tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: tickFontSize }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ paddingTop: '20px' }} />
            {Object.keys(methodsBySetting).map((setting) =>
              selectedLanguages.map(lang => {
                const dataKey = `${setting}_${lang}`;
                return (
                  methodsBySetting[setting].length > 0 && (
                    <Line
                      key={dataKey}
                      type="monotone"
                      dataKey={dataKey}
                      stroke={settingColors[setting]}
                      strokeWidth={2}
                      strokeDasharray={setting === 'monolingual' ? '0' : setting === 'multilingual' ? '5 5' : '10 5'}
                      dot={{ r: 4, fill: colors[lang], stroke: settingColors[setting], strokeWidth: 2 }}
                      name={`${setting} (${lang})`}
                      connectNulls
                    />
                  )
                );
              })
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {Object.entries(settingSummary).map(([setting, data]) => (
          <div
            key={setting}
            className="p-6 rounded-xl shadow-lg border"
            style={{
              backgroundColor: 'var(--card-bg)',
              borderColor: settingColors[setting],
              borderWidth: '2px'
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: settingColors[setting] }}
              />
              <h4 className="text-lg font-bold capitalize" style={{ color: 'var(--text-primary)' }}>
                {setting}
              </h4>
            </div>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              {data.methodCount} experiment{data.methodCount > 1 ? 's' : ''}
            </p>
            <div className="space-y-2">
              {Object.entries(data.languages).map(([lang, stats]) => (
                <div key={lang} className="flex justify-between items-center text-sm">
                  <span style={{ color: colors[lang], fontWeight: 600 }}>{lang}</span>
                  <span style={{ color: 'var(--text-primary)' }}>
                    Avg: {stats.mean.toFixed(1)} | Max: {stats.max.toFixed(1)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Table / Cards */}
      <div className="rounded-xl shadow-lg border overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
        <h4 className="text-lg font-semibold p-4 text-center" style={{ color: 'var(--text-primary)', backgroundColor: 'var(--bg-secondary)' }}>
          Detailed Setting Statistics
        </h4>

        {isMobile ? (
          <div className="p-3 space-y-3">
            {Object.entries(settingSummary).map(([setting, data]) => (
              <div key={setting} className="p-3 rounded-lg border" style={{ borderColor: settingColors[setting], borderWidth: '1px' }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: settingColors[setting] }} />
                  <span className="text-sm font-semibold capitalize" style={{ color: settingColors[setting] }}>{setting}</span>
                </div>
                <div className="space-y-2">
                  {Object.entries(data.languages).map(([lang, stats]) => (
                    <div key={lang} className="flex items-center justify-between text-xs p-2 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                      <span className="font-medium" style={{ color: colors[lang] }}>{lang}</span>
                      <div className="flex gap-3">
                        <span style={{ color: 'var(--text-primary)' }}>Avg: {stats.mean.toFixed(1)}</span>
                        <span style={{ color: 'var(--success-500)' }}>{stats.max.toFixed(1)}</span>
                        <span style={{ color: 'var(--error-500)' }}>{stats.min.toFixed(1)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Setting</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Language</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Mean</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Max</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Min</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Range</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(settingSummary).flatMap(([setting, data]) =>
                  Object.entries(data.languages).map(([lang, stats], idx) => (
                    <tr key={`${setting}-${lang}`} style={{ backgroundColor: idx % 2 === 0 ? 'var(--bg-secondary)' : 'transparent' }}>
                      {idx === 0 && (
                        <td
                          rowSpan={Object.keys(data.languages).length}
                          className="px-4 py-3 font-medium capitalize"
                          style={{ color: settingColors[setting] }}
                        >
                          {setting}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: colors[lang] + '20', color: colors[lang] }}
                        >
                          {lang}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold" style={{ color: 'var(--text-primary)' }}>{stats.mean.toFixed(2)}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--success-500)' }}>{stats.max.toFixed(2)}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--error-500)' }}>{stats.min.toFixed(2)}</td>
                      <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{(stats.max - stats.min).toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingComparisonView;
